import { defaultSettings, initialMockOrders } from './mock'
import { getInitData } from './telegram'
import type { Order, OrderFilters, OrderStatus, ScanRun, Settings } from '../types'

const baseUrl = import.meta.env.VITE_API_BASE_URL as string
const useMocks = import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true'
const wait = (ms = 220) => new Promise((resolve) => setTimeout(resolve, ms))
let mockOrders = structuredClone(initialMockOrders)
let mockSettings = structuredClone(defaultSettings)

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = sessionStorage.getItem('sitelead_session')
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...init?.headers },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(body.error || 'Не удалось выполнить запрос')
  return body as T
}

export const api = {
  isMock: useMocks,
  async authenticate() {
    if (useMocks) { await wait(100); sessionStorage.setItem('sitelead_session', 'local-mock-session'); return }
    const data = await request<{ token: string }>('/auth/telegram', { method: 'POST', body: JSON.stringify({ initData: getInitData() }) })
    sessionStorage.setItem('sitelead_session', data.token)
  },
  async getOrders(filters: OrderFilters) {
    if (useMocks) { await wait(); return structuredClone(mockOrders) }
    const query = new URLSearchParams({ sources: filters.sources.join(','), minBudget: String(filters.minBudget), minRelevance: String(filters.minRelevance), statuses: filters.statuses.join(',') })
    return request<{ items: Order[]; nextCursor: string | null }>(`/orders?${query}`).then((r) => r.items)
  },
  async updateState(id: string, patch: { favorite?: boolean; status?: OrderStatus; response?: string }) {
    if (useMocks) { await wait(120); mockOrders = mockOrders.map((o) => o.id === id ? { ...o, ...patch } : o); return mockOrders.find((o) => o.id === id)! }
    return request<Order>(`/orders/${id}/state`, { method: 'PATCH', body: JSON.stringify(patch) })
  },
  async generateResponse(id: string) {
    if (useMocks) {
      await wait(650)
      const order = mockOrders.find((o) => o.id === id)!
      const response = `Здравствуйте! Изучил задачу «${order.title}». Готов собрать адаптивный, быстрый сайт и аккуратно подключить все формы. Перед стартом уточню детали и дам понятную оценку по срокам.`
      await this.updateState(id, { response })
      return { response }
    }
    return request<{ response: string }>(`/orders/${id}/generate-response`, { method: 'POST' })
  },
  async getSettings() {
    if (useMocks) { await wait(120); return structuredClone(mockSettings) }
    return request<Settings>('/settings')
  },
  async saveSettings(settings: Settings) {
    if (useMocks) { await wait(250); mockSettings = structuredClone(settings); return mockSettings }
    return request<Settings>('/settings', { method: 'PATCH', body: JSON.stringify(settings) })
  },
  async startScan(): Promise<ScanRun> {
    if (useMocks) { await wait(500); return { id: crypto.randomUUID(), status: 'success', startedAt: new Date().toISOString(), finishedAt: new Date().toISOString(), found: 3 } }
    return request<ScanRun>('/scan', { method: 'POST' })
  },
}
